import { attributes, htmlAttributes } from '../../core/ids-attributes';
import { customElement, scss } from '../../core/ids-decorators';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';

import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsKeyboardMixin from '../../mixins/ids-keyboard-mixin/ids-keyboard-mixin';
import IdsElement from '../../core/ids-element';
import '../ids-loading-indicator/ids-loading-indicator';

import styles from './ids-tab-content.scss';

const Base = IdsKeyboardMixin(
  IdsEventsMixin(
    IdsElement
  )
);

/**
 * IDS TabContent Component
 * @type {IdsTabContent}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @mixes IdsKeyboardMixin
 */
@customElement('ids-tab-content')
@scss(styles)
export default class IdsTabContent extends Base {
  private loadingPromise: Promise<void> | null = null;

  private loadTimeout: number | null = null;

  constructor() {
    super();
  }

  // Use mountedCallback for setting aria attributes because connectedCallback fires too early
  mountedCallback(): void {
    this.setAttribute(htmlAttributes.ROLE, 'tabpanel');
    this.setAriaAttributes();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#cleanup();
  }

  #cleanup() {
    if (this.loadTimeout) {
      window.clearTimeout(this.loadTimeout);
      this.loadTimeout = null;
    }

    this.loadingPromise = null;
  }

  /** @returns {Array} The attributes we handle as getters/setters */
  static get attributes() {
    return [
      ...super.attributes,
      attributes.VALUE,
      attributes.ACTIVE,
      attributes.DISABLED,
      attributes.HIDDEN,
      attributes.LAZY,
      attributes.LOADED,
      attributes.LOADING,
      attributes.ERROR,
      attributes.ERROR_MESSAGE
    ];
  }

  template() {
    return `
      <div class="ids-tab-content-wrapper">
        <div class="ids-tab-content-loading" part="loading" hidden>
          <ids-loading-indicator size="medium"></ids-loading-indicator>
        </div>
        <div class="ids-tab-content-error" part="error" hidden>
          <slot name="error">
            <ids-text>Failed to load content.</ids-text>
          </slot>
        </div>
        <div class="ids-tab-content-main" part="content">
          <slot></slot>
        </div>
      </div>
    `;
  }

  /** @param {boolean | string} value If true, disables the tab content and shows overlay */
  set disabled(value: boolean | string) {
    const isDisabled = stringToBool(value);

    if (isDisabled) {
      this.setAttribute(attributes.DISABLED, '');
    } else {
      this.removeAttribute(attributes.DISABLED);
    }
  }

  /** @returns {boolean | string} value If true, disables the tab content and shows overlay */
  get disabled(): boolean | string {
    return this.hasAttribute(attributes.DISABLED);
  }

  /** @param {string} value representing associated tab */
  set value(value) {
    if (this.getAttribute(attributes.VALUE) === value) {
      return;
    }

    this.setAttribute(attributes.VALUE, String(value));
    this.setAriaAttributes();
  }

  /** @returns {string} Value representing associated tab */
  get value() {
    return this.getAttribute(attributes.VALUE) || '';
  }

  /** @param {boolean|string} value Whether or not this tab will be flagged as active/visible */
  set active(value) {
    const isTruthy = stringToBool(value);

    if (isTruthy && !this.hasAttribute(attributes.ACTIVE)) {
      this.setAttribute(attributes.ACTIVE, '');

      if (this.lazy && !this.loaded && !this.loading) {
        if (this.loadTimeout) {
          window.clearTimeout(this.loadTimeout);
        }

        this.loadTimeout = window.setTimeout(() => {
          this.loadTimeout = null;
          this.load().catch((error) => {
            console.error('Lazy loading failed:', error);
          });
        }, 0);
      }
    }

    if (!isTruthy && this.hasAttribute(attributes.ACTIVE)) {
      this.removeAttribute(attributes.ACTIVE);
    }
  }

  /** @returns {boolean|string} Whether or not this tab will be flagged as active/visible */
  get active() {
    return this.hasAttribute(attributes.ACTIVE);
  }

  /** @param {boolean|string} value If true, the tab content will be hidden */
  set hidden(value) {
    const isTruthy = stringToBool(value);

    if (isTruthy && !this.hasAttribute(attributes.HIDDEN)) {
      this.setAttribute(attributes.HIDDEN, '');
      this.removeAttribute(attributes.ACTIVE);
    }

    if (!isTruthy && this.hasAttribute(attributes.HIDDEN)) {
      this.removeAttribute(attributes.HIDDEN);
    }
  }

  /** @returns {boolean} Whether or not this tab content is hidden */
  get hidden() {
    return this.hasAttribute(attributes.HIDDEN);
  }

  /** @param {boolean|string} value If true, enables lazy loading for this tab content */
  set lazy(value) {
    const isTruthy = stringToBool(value);

    if (isTruthy && !this.hasAttribute(attributes.LAZY)) {
      this.setAttribute(attributes.LAZY, '');
    }

    if (!isTruthy && this.hasAttribute(attributes.LAZY)) {
      this.removeAttribute(attributes.LAZY);
    }
  }

  /** @returns {boolean} Whether or not this tab content uses lazy loading */
  get lazy() {
    return this.hasAttribute(attributes.LAZY);
  }

  /** @param {boolean|string} value If true, indicates content has been loaded */
  set loaded(value) {
    const isTruthy = stringToBool(value);

    if (isTruthy && !this.hasAttribute(attributes.LOADED)) {
      this.setAttribute(attributes.LOADED, '');
    }

    if (!isTruthy && this.hasAttribute(attributes.LOADED)) {
      this.removeAttribute(attributes.LOADED);
    }
  }

  /** @returns {boolean} Whether or not this tab content has been loaded */
  get loaded() {
    return this.hasAttribute(attributes.LOADED);
  }

  /** @param {boolean|string} value If true, indicates content is currently loading */
  set loading(value) {
    const isTruthy = stringToBool(value);
    const loadingEl = this.shadowRoot?.querySelector('.ids-tab-content-loading');
    const contentEl = this.shadowRoot?.querySelector('.ids-tab-content-main');

    if (isTruthy && !this.hasAttribute(attributes.LOADING)) {
      this.setAttribute(attributes.LOADING, '');
      this.setAttribute('aria-busy', 'true');
      loadingEl?.removeAttribute('hidden');
      contentEl?.setAttribute('hidden', '');
    }

    if (!isTruthy && this.hasAttribute(attributes.LOADING)) {
      this.removeAttribute(attributes.LOADING);
      this.removeAttribute('aria-busy');
      loadingEl?.setAttribute('hidden', '');
      contentEl?.removeAttribute('hidden');
    }
  }

  /** @returns {boolean} Whether or not this tab content is currently loading */
  get loading() {
    return this.hasAttribute(attributes.LOADING);
  }

  /** @param {boolean|string} value If true, indicates content is in error state */
  set error(value) {
    const isTruthy = stringToBool(value);

    if (isTruthy && !this.hasAttribute(attributes.ERROR)) {
      this.setAttribute(attributes.ERROR, '');
    }

    if (!isTruthy && this.hasAttribute(attributes.ERROR)) {
      this.removeAttribute(attributes.ERROR);
    }
  }

  /** @returns {boolean} Whether or not this tab content is in error state */
  get error() {
    return this.hasAttribute(attributes.ERROR);
  }

  /** @param {string} value The error message to display */
  set errorMessage(value) {
    if (value) {
      this.setAttribute(attributes.ERROR_MESSAGE, String(value));
    } else {
      this.removeAttribute(attributes.ERROR_MESSAGE);
    }
  }

  /** @returns {string} The error message to display */
  get errorMessage() {
    return this.getAttribute(attributes.ERROR_MESSAGE) || 'Failed to load content.';
  }

  /**
   * Loads the tab content if it's lazy and not already loaded
   * @returns {Promise<void>}
   */
  async load(): Promise<void> {
    if (this.loaded) return;

    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = this.#performLoad();
    try {
      await this.loadingPromise;
    } finally {
      this.loadingPromise = null;
    }
  }

  /**
   * Performs the actual loading logic
   * @returns {Promise<void>}
   * @private
   */
  async #performLoad(): Promise<void> {
    if (!this.lazy || this.loaded) return;

    this.loading = true;
    this.#hideError();

    try {
      // Fire tabload event to allow custom loading logic
      const loadEvent = new CustomEvent('tabload', {
        detail: { value: this.value, content: this },
        bubbles: true,
        cancelable: true
      });

      const shouldContinue = this.dispatchEvent(loadEvent);
      if (!shouldContinue) {
        this.loading = false;
        return;
      }

      // Check for template content
      const template = this.querySelector('template');
      if (template) {
        await this.#loadFromTemplate(template);
      }

      this.loaded = true;
      this.loading = false;

      // Fire tabloaded event
      this.dispatchEvent(new CustomEvent('tabloaded', {
        detail: { value: this.value, content: this },
        bubbles: true
      }));
    } catch (error) {
      this.loading = false;
      this.#showError();

      // Fire tabloaderror event
      this.dispatchEvent(new CustomEvent('tabloaderror', {
        detail: { value: this.value, content: this, error },
        bubbles: true
      }));
    }
  }

  /**
   * Reloads the tab content (forces reload even if already loaded)
   * @returns {Promise<void>}
   */
  async reload(): Promise<void> {
    this.loadingPromise = null;
    this.loaded = false;
    await this.load();
  }

  /**
   * Manually trigger error state (useful for testing/demos)
   * @param {Error} error The error to display
   */
  triggerError(error: Error) {
    this.loading = false;
    this.#showError();

    this.dispatchEvent(new CustomEvent('tabloaderror', {
      detail: { value: this.value, content: this, error },
      bubbles: true
    }));
  }

  /**
   * Unloads the tab content (clears loaded content and resets state)
   */
  unload() {
    if (!this.lazy) return;

    this.loaded = false;
    this.loading = false;
    this.#hideError();

    // Clear content but keep template
    const template = this.querySelector('template');
    this.innerHTML = '';
    if (template) {
      this.appendChild(template);
    }
  }

  /**
   * Loads content from a template element
   * @param {HTMLTemplateElement} template The template to load from
   * @private
   */
  async #loadFromTemplate(template: HTMLTemplateElement) {
    const existingTemplate = this.querySelector('template');
    this.innerHTML = '';
    if (existingTemplate) {
      this.appendChild(existingTemplate);
    }

    const content = template.content.cloneNode(true);
    this.appendChild(content);
  }

  /**
   * Shows the error state
   * @private
   */
  #showError() {
    this.error = true;

    const errorEl = this.shadowRoot?.querySelector('.ids-tab-content-error');
    const contentEl = this.shadowRoot?.querySelector('.ids-tab-content-main');

    // Update the default error message if no custom slot content exists
    const errorSlot = errorEl?.querySelector('slot[name="error"]') as HTMLSlotElement;
    const defaultErrorText = errorEl?.querySelector('ids-text');
    if (defaultErrorText && !errorSlot?.assignedElements().length) {
      defaultErrorText.textContent = this.errorMessage;
    }

    errorEl?.removeAttribute('hidden');
    contentEl?.setAttribute('hidden', '');
  }

  /**
   * Hides the error state
   * @private
   */
  #hideError() {
    this.error = false;

    const errorEl = this.shadowRoot?.querySelector('.ids-tab-content-error');
    const contentEl = this.shadowRoot?.querySelector('.ids-tab-content-main');

    errorEl?.setAttribute('hidden', '');
    contentEl?.removeAttribute('hidden');
  }

  /**
   * Sets proper ARIA attributes for the tab panel
   * @private
   */
  private setAriaAttributes(): void {
    if (this.value) {
      this.setAttribute(htmlAttributes.ARIA_LABELLED_BY, this.value);
    }
  }
}
